import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { Bell, Mail, ShoppingBag, UserPlus } from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  "customer.registered": UserPlus,
  "enquiry.contact": Mail,
  "enquiry.custom_design": Mail,
  "order.paid": ShoppingBag,
};

// Small bell that polls every 30s for unread admin notifications.
// Only rendered for staff (non-customer) users.
const NotificationBell = () => {
  const { user } = useApp();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ unread: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    if (!user || user.role === "customer") return;
    const fetch = async () => {
      try {
        const { data } = await api.get("/admin/notifications?limit=20");
        setData(data);
      } catch (e) { /* ignore */ if (typeof console !== "undefined") console.debug("[silent]", e); }
    };
    fetch();
    timerRef.current = setInterval(fetch, 30000);
    return () => clearInterval(timerRef.current);
  }, [user]);

  if (!user || user.role === "customer") return null;

  const markAllRead = async () => {
    setLoading(true);
    try {
      await api.post("/admin/notifications/mark-all-read");
      const { data } = await api.get("/admin/notifications?limit=20");
      setData(data);
      toast.success("All notifications marked as read");
    } catch (e) { /* ignore */ if (typeof console !== "undefined") console.debug("[silent]", e); }
    finally { setLoading(false); }
  };

  const click = async (n) => {
    try { await api.patch(`/admin/notifications/${n.id}/read`); } catch (e) { /* ignore */ if (typeof console !== "undefined") console.debug("[silent]", e); }
    setOpen(false);
    if (n.type?.startsWith("enquiry")) navigate("/admin/enquiries");
    else if (n.type === "order.paid") navigate(`/admin/orders/${n.ref_id}`);
    else if (n.type === "customer.registered") navigate(`/admin/customers/${n.ref_id}`);
  };

  return (
    <div className="relative" data-testid="notif-bell-wrapper">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="notif-bell-btn"
        className="relative p-2 hover:bg-background border border-transparent hover:border-border transition-colors"
        aria-label="Notifications"
      >
        <Bell size={16} />
        {data.unread > 0 && (
          <span data-testid="notif-count-badge" className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-medium">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 border border-border bg-card shadow-lg z-50 max-h-[70vh] overflow-y-auto" data-testid="notif-dropdown">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="overline text-accent">Notifications</div>
            <button onClick={markAllRead} disabled={loading || data.unread === 0} data-testid="notif-mark-all-read" className="text-[11px] text-muted-foreground hover:text-accent disabled:opacity-40">Mark all read</button>
          </div>
          {data.items.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No notifications yet.</div>}
          {data.items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button
                key={n.id}
                onClick={() => click(n)}
                data-testid={`notif-item-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-border flex items-start gap-3 hover:bg-background ${n.read ? "opacity-60" : ""}`}
              >
                <Icon size={14} className="text-accent mt-1 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{n.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{n.message}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</div>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-accent shrink-0 mt-2" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
